import React, { useRef, useState } from 'react';
import { Button, Modal, Image } from 'semantic-ui-react';
import { CameraIcon, CameraOffIcon, Loader2Icon } from 'lucide-react';
const CameraCapture = ({ onPicture, background }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const startCamera = async () => {
        setIsModalOpen(true);
        setCapturedImage(null);
        setCameraError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
                setIsCameraActive(true);
            }
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'NotAllowedError') {
                setCameraError('Camera permission was denied. Please allow camera access in your browser settings and try again.');
            }
            else {
                setCameraError('Could not access the camera. Please check that your device has a camera connected.');
            }
        }
    };
    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            stream.getTracks().forEach(track => track.stop());
        }
        setIsCameraActive(false);
        setIsModalOpen(false);
        setCapturedImage(null);
        setCameraError(null);
    };
    const capturePicture = () => {
        if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            const video = videoRef.current;
            if (context) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    if (!blob) {
                        console.error('Error capturing picture');
                        return;
                    }
                    const imageUrl = URL.createObjectURL(blob);
                    setCapturedImage(imageUrl);
                    setIsModalOpen(false);
                    setIsProcessing(true);
                    const handleImageProcessing = async (imageFile) => {
                        try {
                            await onPicture(imageFile);
                        }
                        finally {
                            setTimeout(() => {
                                setIsProcessing(false);
                            }, 500); // Small delay to ensure user sees the processing state
                        }
                    };
                    handleImageProcessing(new File([blob], 'image.jpg', { type: 'image/jpeg' }));
                }, 'image/jpeg', 1);
                stopCamera();
                setCapturedImage(null);
                setIsModalOpen(false);
            }
        }
    };
    let backgroundClass = background === 'white' ? 'bg-white' : 'bg-gray-900';
    let textColor = background === 'white' ? 'text-gray-600' : 'text-white';
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { onClick: startCamera, disabled: isProcessing, className: `w-10 h-10 rounded-full ${backgroundClass} border border-gray-200 hover:border-orange-300 hover:bg-orange-50 flex items-center justify-center transition-all duration-300 group shadow-sm ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`, title: isProcessing ? "Processing..." : "Camera capture" }, isProcessing ? (React.createElement(Loader2Icon, { className: `w-5 h-5 ${textColor} animate-spin` })) : (React.createElement(CameraIcon, { className: `w-5 h-5 ${textColor} group-hover:text-orange-500` }))),
        React.createElement(Modal, { open: isModalOpen, onClose: () => stopCamera() },
            React.createElement(Modal.Header, null, "Captured Image"),
            React.createElement(Modal.Content, null, cameraError ? (React.createElement("div", { className: "flex flex-col items-center gap-3 py-10 px-4" },
                React.createElement(CameraOffIcon, { className: "w-10 h-10 text-gray-400" }),
                React.createElement("p", { className: "text-gray-600 text-center text-sm max-w-xs" }, cameraError))) : (React.createElement(React.Fragment, null,
                React.createElement("video", { ref: videoRef, style: { width: '100%', height: 'auto' } }),
                React.createElement("canvas", { ref: canvasRef, style: { display: 'none' } }),
                capturedImage && React.createElement(Image, { src: capturedImage, size: 'large' })))),
            React.createElement(Modal.Actions, null,
                !cameraError && React.createElement(Button, { onClick: capturePicture }, "Capture"),
                React.createElement(Button, { onClick: () => stopCamera() }, "Close")))));
};
export default CameraCapture;
